import { motion } from 'framer-motion';
import { Image as ImageIcon } from 'lucide-react';

const Gallery = () => {
  // Replace these URLs with your own images
  // Upload your images to /app/frontend/public/images/ folder
  // Then use: '/images/your-image.jpg'
  
  const images = [
    {
      // Change to: url: '/images/your-sedan.jpg',
      url: 'https://images.unsplash.com/photo-1727309842601-19c2d9e42a67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxzZWRhbiUyMGNhciUyMHNpZGUlMjB2aWV3JTIwd2hpdGUlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc2OTMzNDIzOHww&ixlib=rb-4.1.0&q=85',
      alt: 'Premium Sedan Taxi',
      category: 'Fleet',
      tall: false
    },
    {
      // Change to: url: '/images/your-suv.jpg',
      url: 'https://images.pexels.com/photos/14649121/pexels-photo-14649121.jpeg',
      alt: 'Spacious SUV Taxi',
      category: 'Fleet',
      tall: true
    },
    {
      // Change to: url: '/images/dehradun-view.jpg',
      url: 'https://images.unsplash.com/photo-1703693620961-863d96247208?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxkZWhyYWR1biUyMGxhbmRzY2FwZSUyMG1vdW50YWluc3xlbnwwfHx8fDE3NjkzMzQyNDN8MA&ixlib=rb-4.1.0&q=85',
      alt: 'Beautiful Dehradun Landscape',
      category: 'Destinations',
      tall: false
    },
    {
      // Change to: url: '/images/happy-family.jpg',
      url: 'https://images.unsplash.com/photo-1761597803608-f862d2954885?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGZhbWlseSUyMHRvdXJpc3RzJTIwbW91bnRhaW5zfGVufDB8fHx8MTc2OTMzNDI0MXww&ixlib=rb-4.1.0&q=85',
      alt: 'Family enjoying vacation',
      category: 'Happy Customers',
      tall: false
    },
    {
      // Change to: url: '/images/tourists.jpg',
      url: 'https://images.unsplash.com/photo-1642945543180-849ec3b93cef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGZhbWlseSUyMHRvdXJpc3RzJTIwbW91bnRhaW5zfGVufDB8fHx8MTc2OTMzNDI0MXww&ixlib=rb-4.1.0&q=85',
      alt: 'Happy tourists enjoying mountains',
      category: 'Happy Customers',
      tall: true
    },
    {
      // Change to: url: '/images/mussoorie.jpg',
      url: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=800',
      alt: 'Mussoorie Hill Station',
      category: 'Destinations',
      tall: false
    }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="gallery-page">
      {/* Hero Section */}
      <section className="gradient-hero py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="inline-flex items-center justify-center bg-blue-100 p-4 rounded-2xl mb-4">
              <ImageIcon className="w-10 h-10 text-blue-600" />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 leading-tight tracking-tight">
              Our Gallery
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Explore our fleet, destinations, and happy customers enjoying their journeys with us.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 auto-rows-[200px]" data-testid="gallery-grid">
            {images.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`gallery-item-${index}`}
                className={`relative group overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 ${
                  image.tall ? 'md:row-span-2' : 'md:row-span-1'
                } ${index === 0 ? 'md:col-span-2' : 'md:col-span-1'}`}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <span className="inline-block bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full mb-2">
                      {image.category}
                    </span>
                    <p className="text-white font-semibold text-lg">{image.alt}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Categories */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border border-blue-100">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Fleet</h3>
              <ul className="space-y-2 text-slate-600">
                <li>✓ Premium Sedans</li>
                <li>✓ Spacious SUVs</li>
                <li>✓ Tempo Traveller</li>
                <li>✓ Well-maintained vehicles</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-2xl border border-orange-100">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Popular Destinations</h3>
              <ul className="space-y-2 text-slate-600">
                <li>✓ Mussoorie</li>
                <li>✓ Rishikesh</li>
                <li>✓ Haridwar</li>
                <li>✓ Delhi & beyond</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-2xl border border-green-100">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Happy Customers</h3>
              <ul className="space-y-2 text-slate-600">
                <li>✓ 10,000+ satisfied customers</li>
                <li>✓ 5-star ratings</li>
                <li>✓ Safe & comfortable trips</li>
                <li>✓ Memorable journeys</li>
              </ul>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16 text-center"
          >
            <p className="text-lg text-slate-600 mb-6">
              Ready to create your own memorable journey?
            </p>
            <a
              href="tel:+916396867682"
              data-testid="gallery-cta-btn"
              className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-bold px-10 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-orange-500/30 hover:-translate-y-0.5 uppercase tracking-wide"
            >
              Book Now
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
